import React from 'react';
import { CheckCircle, Target, Shield, Users } from 'lucide-react';

const WhyIndustryExpertise = () => {
  const reasons = [
    {
      icon: Target,
      title: 'Industry-Specific Solutions',
      description: 'Customized IT networking and cybersecurity solutions designed specifically for your industry\'s unique requirements and challenges.',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Shield,
      title: 'Compliance Expertise',
      description: 'Deep understanding of industry regulations including HIPAA, PCI-DSS, SOC 2, and other compliance standards.',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: Users,
      title: 'Proven Track Record',
      description: 'Successfully delivered IT solutions to numerous clients in your industry with measurable results.',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      icon: CheckCircle,
      title: 'Best Practices',
      description: 'Implementation of industry best practices and standards to ensure optimal security and performance.',
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <section className="section-padding bg-secondary-50">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Why Our <span className="text-gradient">Industry Expertise</span> Matters
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            We don't just provide generic IT solutions. Our team understands the specific challenges, 
            regulations, and requirements of your industry.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            return (
              <div
                key={reason.title}
                className="card-hover group text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-8">
                  <div className={`w-16 h-16 ${reason.bgColor} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-8 h-8 ${reason.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-4">
                    {reason.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="bg-surface rounded-2xl p-12 shadow-elevation-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-text-primary mb-6">
                Your Industry, Our Priority
              </h3>
              <p className="text-text-secondary mb-6 leading-relaxed">
                At Packet Path, we understand that every industry has unique IT networking and 
                cybersecurity requirements. Our team stays current with industry-specific regulations, 
                emerging threats, and best practices to deliver solutions that truly meet your needs.
              </p>
              <ul className="space-y-4">
                {[
                  'Regulatory compliance assurance',
                  'Industry-specific security protocols',
                  'Customized network architecture',
                  'Ongoing support and monitoring',
                  'Scalable solutions for growth'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-tech rounded-xl p-8 text-white">
              <h4 className="text-xl font-bold mb-6">Get Industry-Specific IT Solutions</h4>
              <p className="text-white/90 mb-8">
                Schedule a consultation with our industry experts to discuss your specific 
                IT networking and cybersecurity needs.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-2xl font-bold">1</span>
                  </div>
                  <span>Free consultation and assessment</span>
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-2xl font-bold">2</span>
                  </div>
                  <span>Custom solution proposal</span>
                </div>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-2xl font-bold">3</span>
                  </div>
                  <span>Implementation and ongoing support</span>
                </div>
              </div>
              <a href="/contact" className="mt-8 btn-secondary bg-white text-primary hover:bg-blue-50 inline-block">
                Schedule Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyIndustryExpertise;
